import { puede, type Usuario, useUsuario } from "@/features/acceso/api";
import { useResaleProducts } from "@/features/bodega/productApi";
import { veProducto } from "@/features/bodega/productLabels";
import type { Quality, ResaleProduct } from "@/features/bodega/productTypes";
import { useLisaPrices, useLisaReference } from "@/features/comercial/api";
import type { LisaPrices } from "@/features/comercial/tipos/lisa";
import type { SaleUnit } from "@/features/comercial/types";

import { useLineReferences, useQuoteOptions } from "../../api";
import type { LineReferences, QuoteOptions } from "../../types";

import { useItemsManuales } from "./items-manuales";

/** En qué se vende; ml solo en Baldosa (#1/#4). */
type Unit = SaleUnit;

/**
 * **Una combinación vendible de Costos › Referencias**: referencia × formato × color o receta, con el
 * precio sugerido por unidad tal como lo da el servidor. La pantalla no lo recalcula (D-041).
 */
export type Combinacion = {
  key: string;
  /** La línea, con el nombre de Costos › Referencias: «Pegante», «Calado», «Baldosa»… */
  line: string;
  /** Lo que se lee en el buscador: «Boquilla · Bolsa 5 kg · Morada A1». */
  label: string;
  reference_id: string;
  reference_name: string;
  format_name: string | null;
  /** El color o la receta de la ficha (`family_variant_id`); la lisa no tiene: lleva su color. */
  family_variant_id: string | null;
  variant_name: string | null;
  color_id: string | null;
  color_code: string | null;
  hex: string | null;
  /** La primera es la sugerida. */
  sale_units: Unit[];
  price: Partial<Record<Unit, string | null>>;
  /** La reventa (0145): sale de lo disponible de la Bodega de producto, en esta calidad («de la Bodega»). */
  finished_stock?: Quality;
  /** Lo que se lee junto a la línea: «12 disponibles», o el código del ítem manual. */
  detail?: string;
  /** El precio sugerido es el último cotizado (reventa, ítem manual): se lee «Último: …». */
  last_price?: true;
};

/** Sin tildes, en minúscula y con «x» por «×»: «20x20» encuentra «20×20» y «ceramico» «Cerámicos». */
export const normalizar = (text: string) =>
  text.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().replace(/×/g, "x");

/** Las combinaciones de una línea con referencias estándar: una por referencia y variante. */
export function combinacionesDeLinea(data: LineReferences): Combinacion[] {
  return data.references.flatMap((ref) => ref.variants.map((v): Combinacion => ({
    key: `${ref.reference_id}:${v.variant_id}`,
    line: data.line.name,
    label: `${ref.name} · ${v.name}`,
    reference_id: ref.reference_id,
    reference_name: ref.name,
    format_name: ref.format_name,
    family_variant_id: v.variant_id,
    variant_name: v.name,
    color_id: v.color_id,
    color_code: v.color_code,
    hex: v.hex,
    sale_units: ref.sale_units.length ? ref.sale_units : ["und"],
    price: v.price,
  })));
}

/**
 * La baldosa lisa, un color por combinación («Baldosa Lisa 20×20 · A1»), con el precio por m² de su
 * carta (`/quotations/lisa-prices`). El color se guarda por id: la carta da el código y las opciones
 * del Cotizador lo traducen.
 */
export function combinacionesDeLisa(reference: { id: string; name: string }, prices: LisaPrices, colors: QuoteOptions["colors"]): Combinacion[] {
  const idOf = new Map(colors.map((c) => [c.code, c.id]));
  return (Array.isArray(prices.colors) ? prices.colors : []).map((color): Combinacion => ({
    key: `${reference.id}:${color.code}`,
    line: "Baldosa",
    label: `${reference.name} · ${color.code}`,
    reference_id: reference.id,
    reference_name: reference.name,
    format_name: prices.format.name,
    family_variant_id: null,
    variant_name: null,
    color_id: idOf.get(color.code) ?? null,
    color_code: color.code,
    hex: color.hex,
    sale_units: ["m2"],
    price: { m2: color.price_m2 },
  }));
}

/**
 * **La reventa** (0145): lo activado para la venta en la Bodega de producto, uno por producto, en
 * unidades, con el último precio cotizado (el que guardó la última cotización) como sugerido, y su
 * código NECA-OT-NN (0153) junto a lo disponible: con él también se encuentra.
 */
export function combinacionesDeReventa(rows: ResaleProduct[]): Combinacion[] {
  return rows.map((r): Combinacion => ({
    key: `reventa:${r.reference_id}`,
    line: "Bodega de producto",
    label: r.reference_name,
    reference_id: r.reference_id,
    reference_name: r.reference_name,
    format_name: null,
    family_variant_id: null,
    variant_name: null,
    color_id: null,
    color_code: null,
    hex: null,
    sale_units: ["und"],
    price: { und: r.last_price?.sale_unit === "und" ? r.last_price.unit_price : null },
    finished_stock: "first",
    detail: [r.siigo_code, `${Number(r.available_pieces).toLocaleString("es-CO")} disponibles`].filter(Boolean).join(" · "),
    last_price: true,
  }));
}

/** Las que tienen todas las palabras escritas, en cualquier orden: «lisa a1 20x20»; también el código (NECA-OT-NN) del detalle. */
export function buscar(items: Combinacion[], text: string): Combinacion[] {
  const words = normalizar(text).split(/\s+/).filter(Boolean);
  if (!words.length) return items;
  return items.filter((item) => {
    const haystack = normalizar(`${item.line} ${item.label} ${item.format_name ?? ""} ${item.color_code ?? ""} ${item.detail ?? ""}`);
    return words.every((w) => haystack.includes(w));
  });
}

/**
 * La reventa la lee quien ve la Bodega de producto o quien cotiza (corrección 14-oct: Ventas cotiza la
 * reventa); sin permiso, el buscador sigue igual.
 */
function leeReventa(usuario: Usuario | null | undefined): boolean {
  return veProducto(usuario) || puede(usuario, "cotizador.ver");
}

/**
 * **Todas las combinaciones vendibles**, para el buscador de la hoja: las de Pegante, Fundidos,
 * Cerámicos y Calado (`GET /quotations/line-references?line=`), la lisa por color, la reventa de la
 * Bodega de producto (`GET /finished-stock/resale`, 0145) y los ítems manuales de la línea «Otro»
 * (`GET /quotations/manual-items`, adenda D del 14-oct). Solo pregunta con
 * el buscador abierto (`enabled`). Una línea que falla no tapa a las demás: se dice cuáles faltan.
 */
export function useCatalogoGlobal(enabled: boolean) {
  // Las líneas con referencias estándar (Fase 4); la misma consulta (y caché) que sus paneles.
  const on = <T,>(value: T) => (enabled ? value : null);
  const lines = [useLineReferences(on("adhesive")), useLineReferences(on("cast")), useLineReferences(on("ceramic")), useLineReferences(on("lattice"))];
  const lisa = useLisaReference(enabled);
  const lisaPrices = useLisaPrices(on(lisa.data?.format_id));
  const options = useQuoteOptions(enabled);
  const seesProduct = leeReventa(useUsuario().data);
  const resale = useResaleProducts(enabled && seesProduct);
  const manual = useItemsManuales(enabled);

  const lisaItems = lisa.data && lisaPrices.data && options.data ? combinacionesDeLisa(lisa.data, lisaPrices.data, options.data.colors) : [];
  const resaleItems = Array.isArray(resale.data?.rows) ? combinacionesDeReventa(resale.data.rows) : [];
  const items = [...lines.flatMap((q) => (Array.isArray(q.data?.references) ? combinacionesDeLinea(q.data) : [])), ...lisaItems, ...resaleItems, ...manual.items];
  const lisaPending = lisa.isPending || (!!lisa.data?.format_id && lisaPrices.isPending);
  const pending = enabled && [lines.some((q) => q.isPending), lisaPending, resale.isLoading, manual.pending].some(Boolean);
  const failed = lines.filter((q) => q.isError).length + Number(lisa.isError || lisaPrices.isError) + Number(resale.isError) + Number(manual.failed);
  return { items, pending, failed };
}
